/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import {
	Badge,
	Flex,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	Table,
	TableContainer,
	Tbody,
	Td,
	Th,
	Thead,
	Tr,
	Tooltip,
	Text,
	Button,
	ModalFooter,
	Textarea,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import DownloadAndViewButton from '../../../components/Files/DownloadAndViewButton';
import DataLoading from '../../../components/DataLoading';
import CompanyName from '../../../components/CompanyName';
import LocalizedDate from '../../../components/LocalizedDate';
import c from '../../../constant';
import useStore from '../../../store';

const AddToBlackList = gql`
	mutation addToBlackList($blackListReasonPublic: String, $blackListReason: String, $id: String!) {
		addToBlackListCompanySaleProfessional(id: $id, blackListReason: $blackListReason, blackListReasonPublic: $blackListReasonPublic)
	}
`;

const RemoveFromBlackList = gql`
	mutation removeFromBlackList($reason: String, $id: String!) {
		removeFromBlackListCompanySaleProfessional(id: $id, reason: $reason)
	}
`;

const QUERY_DATA = gql`
	query get($id: String!) {
		getCompanySaleProfessionalById(id: $id) {
			id
			idCardNumber
			firstName
			lastName
			firstNameEn
			lastNameEn
			gender
			dob
			company {
				id
				name
				nameKhmer
				logo
				type
			}
			intermediarySaleProfessional {
				id
				createdAt
				order
				deleted
				active
				intermediary {
					id
					name
					nameKhmer
					logo
					type
				}
				branch {
					id
					name
					nameLatin
				}
			}
			status
			reason
			internalReason
			baseCertificate
			professionalCertificate
			contract
			requestId
			terminationId
			blackListData {
				id
				active
				blackListReason
				blackListReasonPublic
				createdAt
			}
		}
	}
`;

function SaleStaffModal({ isOpen = false, onClose = () => null, id = null, onActionBlacklist = () => null }) {
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state?.currentLang);
	const [loadData, { loading, data }] = useLazyQuery(QUERY_DATA);

	const [addToBlackList, { data: addToBlackListData }] = useMutation(AddToBlackList);
	const [openAddToBlackListDialog, setOpenAddToBlackListDialog] = useState(false);
	const [reason, setReason] = useState('');
	const [reasonUnblacklist, setReasonUnblacklist] = useState('');
	const [reasonForPublic, setReasonForPublic] = useState('');

	const [openUnblackListDialog, setOpenUnblackListDialog] = useState(false);

	const [removeFromBlackList, { data: removeFromBlackListData }] = useMutation(RemoveFromBlackList);

	useEffect(() => id && loadData({ variables: { id } }), [id]);

	useEffect(() => {
		if (addToBlackListData || removeFromBlackListData) {
			setOpenAddToBlackListDialog(false);
			setOpenUnblackListDialog(false);
			loadData({ variables: { id } });
			onActionBlacklist();
		}
	}, [addToBlackListData, removeFromBlackListData]);

	function getStatus(status, requestId, terminationId) {
		switch (status) {
			case 'active':
				return (
					<Link to={`/company-sale-professional/new/detail?id=${requestId}`}>
						<Badge colorScheme="green">{t('ACTIVE')}</Badge>
					</Link>
				);
			case 'terminated':
				return (
					<Link to={`/company-sale-professional/termination/detail?id=${terminationId}`}>
						<Badge colorScheme="red">{t('Terminated')}</Badge>
					</Link>
				);
			default:
				return null;
		}
	}

	return (
		<>
			<Modal size="4xl" isOpen={isOpen} onClose={onClose} scrollBehavior="outside">
				<ModalOverlay />
				<ModalContent borderRadius={c.borderRadius} pt="2">
					<ModalHeader></ModalHeader>
					<ModalCloseButton />
					<ModalBody pl={0} pr={0} pb={0}>
						<DataLoading loading={loading} loadedData={data?.getCompanySaleProfessionalById}>
							{({ loadedData }) => (
								<TableContainer>
									<Table size="sm">
										<Thead>
											<Tr>
												<Th></Th>
												<Th isNumeric></Th>
											</Tr>
										</Thead>
										<Tbody>
											<Tr bg={data?.getCompanySaleProfessionalById?.blackListData?.active ? 'rgba(192, 57, 43,1.0)' : 'white'}>
												<Td>{t('ID Card Number')}</Td>
												<Td isNumeric fontWeight="bold">
													{loadedData?.idCardNumber}
												</Td>
											</Tr>
											<Tr>
												<Td>{t('First Name')}</Td>
												<Td isNumeric fontWeight="bold" fontFamily="NotoSansKhmer">
													{loadedData?.firstName}
												</Td>
											</Tr>
											<Tr>
												<Td>{t('Last Name')}</Td>
												<Td isNumeric fontWeight="bold" fontFamily="NotoSansKhmer">
													{loadedData?.lastName}
												</Td>
											</Tr>
											<Tr>
												<Td>{t('First Name En')}</Td>
												<Td isNumeric fontWeight="bold">
													{loadedData?.firstNameEn}
												</Td>
											</Tr>
											<Tr>
												<Td>{t('Last Name En')}</Td>
												<Td isNumeric fontWeight="bold">
													{loadedData?.lastNameEn}
												</Td>
											</Tr>
											<Tr>
												<Td>{t('Gender')}</Td>
												<Td isNumeric fontWeight="bold">
													{t(loadedData?.gender)}
												</Td>
											</Tr>
											<Tr>
												<Td>{t('DOB')}</Td>
												<Td isNumeric fontWeight="bold">
													<LocalizedDate dateString={loadedData?.dob} dateFormat={c.dateOnlyFormat} />
												</Td>
											</Tr>
											<Tr>
												<Td>{t('Insurance Education Certificate')}</Td>
												<Td isNumeric fontWeight="bold">
													<Flex justifyContent="flex-end" alignItems="center">
														<DownloadAndViewButton
															marginLeft={0}
															url={loadedData?.baseCertificate?.url}
															size={loadedData?.baseCertificate?.size}
														/>
													</Flex>
												</Td>
											</Tr>
											<Tr>
												<Td>{t('Specialized Certificate')}</Td>
												<Td isNumeric fontWeight="bold">
													<Flex justifyContent="flex-end" alignItems="center">
														<DownloadAndViewButton
															marginLeft={0}
															url={loadedData?.professionalCertificate?.url}
															size={loadedData?.professionalCertificate?.size}
														/>
													</Flex>
												</Td>
											</Tr>
											<Tr>
												<Td>{t('Contract')}</Td>
												<Td isNumeric fontWeight="bold">
													<Flex justifyContent="flex-end" alignItems="center">
														<DownloadAndViewButton
															marginLeft={0}
															url={loadedData?.contract?.url}
															size={loadedData?.contract?.size}
														/>
													</Flex>
												</Td>
											</Tr>
											<Tr>
												<Td>{t('Company')}</Td>
												<Td isNumeric fontWeight="bold">
													<Flex justifyContent="flex-end" alignItems="center">
														<CompanyName company={loadedData?.company} />
													</Flex>
												</Td>
											</Tr>
											<Tr>
												<Td>{t('Status')}</Td>
												<Td isNumeric fontWeight="bold">
													{loadedData?.blackListData?.active ? (
														<Tooltip
															label={
																<Text color="white">
																	{t('Blacklist reason')}: {loadedData?.blackListData?.blackListReason || 'N/A'}
																	<br />
																	{t('Blacklist reason public')}: {loadedData?.blackListData?.blackListReasonPublic || 'N/A'}
																</Text>
															}
														>
															<Badge mr="1" colorScheme="red">
																{t('Blacklist')}
															</Badge>
														</Tooltip>
													) : null}
													{getStatus(
														loadedData?.status === 'approved' ? 'active' : loadedData?.status,
														loadedData?.requestId,
														loadedData?.terminationId,
													)}
												</Td>
											</Tr>
											{loadedData?.reason && (
												<Tr>
													<Td>{t('Reason')}</Td>
													<Td isNumeric fontWeight="bold">
														{loadedData?.reason}
													</Td>
												</Tr>
											)}
											{loadedData?.internalReason && (
												<Tr>
													<Td>{t('Internal reason')}</Td>
													<Td isNumeric fontWeight="bold">
														{loadedData?.internalReason}
													</Td>
												</Tr>
											)}
											{loadedData?.intermediarySaleProfessional?.intermediary && (
												<Tr>
													<Td>{t('Intermediary Company Name')}</Td>
													<Td isNumeric fontWeight="bold">
														<Flex justifyContent="flex-end" alignItems="center">
															<CompanyName company={loadedData?.intermediarySaleProfessional?.intermediary} />
														</Flex>
													</Td>
												</Tr>
											)}
											{loadedData?.intermediarySaleProfessional?.branch && (
												<Tr>
													<Td>{t('Branch')}</Td>
													<Td isNumeric fontWeight="bold">
														{lang === 'kh'
															? loadedData?.intermediarySaleProfessional?.branch?.name ||
																loadedData?.intermediarySaleProfessional?.branch?.nameLatin
															: loadedData?.intermediarySaleProfessional?.branch?.nameLatin ||
																loadedData?.intermediarySaleProfessional?.branch?.name}
													</Td>
												</Tr>
											)}
											<Tr>
												<Td>{t('Actions')}</Td>
												<Td isNumeric>
													{loadedData?.blackListData?.active ? (
														<Button colorScheme="green" onClick={() => setOpenUnblackListDialog(true)}>
															{t('Unblacklist')}
														</Button>
													) : (
														<Button colorScheme="red" onClick={() => setOpenAddToBlackListDialog(true)}>
															{t('Blacklist')}
														</Button>
													)}
												</Td>
											</Tr>
										</Tbody>
									</Table>
								</TableContainer>
							)}
						</DataLoading>
					</ModalBody>
				</ModalContent>
			</Modal>

			<Modal isOpen={openAddToBlackListDialog} onClose={() => setOpenAddToBlackListDialog(false)}>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader>{t('Blacklist')}</ModalHeader>
					<ModalCloseButton />
					<ModalBody pb={6}>
						<Text mb="8px">{t('Reason')}:</Text>
						<Textarea onChange={(e) => setReason(e.currentTarget.value)} placeholder="" size="sm" />

						<Text mt="8px" mb="8px">
							{t('Reason for public')}:
						</Text>
						<Textarea onChange={(e) => setReasonForPublic(e.currentTarget.value)} placeholder="" size="sm" />
					</ModalBody>
					<ModalFooter>
						<Button
							onClick={() =>
								addToBlackList({
									variables: {
										id,
										blackListReason: reason,
										blackListReasonPublic: reasonForPublic,
									},
								})
							}
							colorScheme="red"
						>
							{t('Blacklist')}
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>

			<Modal isOpen={openUnblackListDialog} onClose={() => setOpenUnblackListDialog(false)}>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader>{t('Unblacklist')}</ModalHeader>
					<ModalCloseButton />
					<ModalBody pb={6}>
						<Text mb="8px">{t('Reason')}:</Text>
						<Textarea onChange={(e) => setReasonUnblacklist(e.currentTarget.value)} placeholder="" size="sm" />
					</ModalBody>
					<ModalFooter>
						<Button
							onClick={() =>
								removeFromBlackList({
									variables: {
										id: data?.getCompanySaleProfessionalById?.blackListData?.id,
										reason: reasonUnblacklist,
									},
								})
							}
							colorScheme="red"
						>
							{t('Unblacklist')}
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</>
	);
}

export default SaleStaffModal;
